"use client"

import { createContext, useContext, useEffect, useState, type ReactNode } from "react"
import { useAuth } from "@/contexts/auth-context"
import type { FeatureFlag, FeatureFlags } from "@/types/feature-flags"
import { useLanguage } from "@/contexts/language-context"
import { featureFlagService } from "@/lib/feature-flag-service"
import { env } from "@/lib/env"

type FeatureFlagContextType = {
  featureFlags: FeatureFlags
  isLoading: boolean
  error: string | null
  isFeatureEnabled: (featureName: string) => boolean
  rawFeatureFlags: FeatureFlag[]
}

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(undefined)

export function FeatureFlagProvider({ children }: { children: ReactNode }) {
  const { token, isAuthenticated } = useAuth()
  const { dictionary } = useLanguage()
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>({})
  const [rawFeatureFlags, setRawFeatureFlags] = useState<FeatureFlag[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  // Load feature flags when the application loads and authentication is available
  useEffect(() => {
    const loadFeatureFlags = async () => {
      if (!isAuthenticated) {
        setIsLoading(false)
        setError(dictionary?.featureFlags?.errors?.notAuthenticated || "Authentication required to load feature flags")
        return
      }

      try {
        setIsLoading(true)
        setError(null)

        // Use the feature flag service to get the flags
        const response = await featureFlagService.getFeatureFlags(token)

        // Store the raw feature flags
        setRawFeatureFlags(response)

        // Convert the array to a map of feature name -> flag value
        const flagsMap: FeatureFlags = {}
        response.forEach((flag) => {
          if (!flag.IsDeleted && flag.Data && flag.Data.FeatureName) {
            flagsMap[flag.Data.FeatureName] = flag.Data.Flag
          }
        })

        setFeatureFlags(flagsMap)
      } catch (err) {
        console.error("Failed to load feature flags:", err)
        setError(
          dictionary?.featureFlags?.errors?.loadFailed || "Failed to load feature flags. Please try again later.",
        )
      } finally {
        setIsLoading(false)
      }
    }

    loadFeatureFlags()
  }, [isAuthenticated, token, dictionary])

  // Function to check if a feature is enabled
  const isFeatureEnabled = (featureName: string): boolean => {
    return !!featureFlags[featureName]
  }

  return (
    <FeatureFlagContext.Provider
      value={{ featureFlags, isLoading, error, isFeatureEnabled, rawFeatureFlags }}
    >
      {children}
    </FeatureFlagContext.Provider>
  )
}

export function useFeatureFlags() {
  const context = useContext(FeatureFlagContext)
  if (context === undefined) {
    throw new Error("useFeatureFlags must be used within a FeatureFlagProvider")
  }
  return context
} 